import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, RotateCcw, Trophy, ChevronRight, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { useCourse } from '../hooks/useCourse';
import { useAuth } from '../hooks/useAuth';
import { logActivity, saveFlashcardResult, getFlashcardProgress } from '../lib/supabase';
import { VocabSet, VocabPair, FlashcardProgress, Course } from '../types';
import { LoadingSpinner } from './LoadingSpinner';
import { getCourseProgress, getNextRecommendation, Recommendation } from '../lib/recommendations';
import { RecommendationCard } from './RecommendationCard';

// Shuffle array helper
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Web Audio API Sound Effects
const audioContext = typeof window !== 'undefined'
  ? new (window.AudioContext || (window as typeof window & { webkitAudioContext: typeof AudioContext }).webkitAudioContext)()
  : null;

function playSound(type: 'correct' | 'wrong' | 'complete') {
  if (!audioContext) return;
  if (audioContext.state === 'suspended') audioContext.resume();

  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  const now = audioContext.currentTime;

  switch (type) {
    case 'correct':
      oscillator.frequency.setValueAtTime(600, now);
      oscillator.frequency.setValueAtTime(800, now + 0.05);
      gainNode.gain.setValueAtTime(0.15, now);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
      oscillator.start(now);
      oscillator.stop(now + 0.1);
      break;
    case 'wrong':
      oscillator.frequency.setValueAtTime(300, now);
      gainNode.gain.setValueAtTime(0.1, now);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
      oscillator.start(now);
      oscillator.stop(now + 0.1);
      break;
    case 'complete':
      // Celebration sound
      oscillator.frequency.setValueAtTime(523, now);
      oscillator.frequency.setValueAtTime(659, now + 0.1);
      oscillator.frequency.setValueAtTime(784, now + 0.2);
      gainNode.gain.setValueAtTime(0.15, now);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
      oscillator.start(now);
      oscillator.stop(now + 0.3);
      break;
  }
}

interface MatchItem {
  id: string;
  text: string;
  type: 'english' | 'german';
  pairId: number;
}

// Track result per pair
interface PairResult {
  pair: VocabPair;
  pairIndex: number;
  errorsBeforeMatch: number;
  firstTryCorrect: boolean;
}

function SetSelector({
  sets,
  onSelect,
  weaknessMap,
}: {
  sets: VocabSet[];
  onSelect: (set: VocabSet) => void;
  weaknessMap: Map<string, number>;
}) {
  // Sort sets so those with weaknesses appear first
  const sortedSets = [...sets].sort((a, b) => {
    const aWeak = weaknessMap.get(a.id) || 0;
    const bWeak = weaknessMap.get(b.id) || 0;
    return bWeak - aWeak;
  });

  return (
    <div style={{ padding: '16px', paddingBottom: '100px' }}>
      <h2 style={{
        color: '#fff',
        fontSize: '20px',
        marginBottom: '8px',
      }}>
        Vokabel-Sets
      </h2>
      <p style={{
        color: 'rgba(255,255,255,0.6)',
        fontSize: '14px',
        marginBottom: '24px',
      }}>
        Wähle ein Set zum Üben
      </p>

      <div>
        {sortedSets.map((set) => {
          const weakCount = weaknessMap.get(set.id) || 0;
          return (
            <button
              key={set.id}
              onClick={() => onSelect(set)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                padding: '16px',
                background: weakCount > 0
                  ? 'rgba(255, 152, 0, 0.1)'
                  : 'rgba(255, 255, 255, 0.05)',
                border: weakCount > 0
                  ? '1px solid rgba(255, 152, 0, 0.3)'
                  : '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                cursor: 'pointer',
                textAlign: 'left',
                marginBottom: '10px',
                transition: 'all 0.2s ease',
              }}
            >
              <span style={{ fontSize: '28px' }}>{set.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                  <span style={{
                    color: '#e8eaf6',
                    fontSize: '16px',
                    fontWeight: 600,
                  }}>
                    {set.title}
                  </span>
                  {weakCount > 0 && (
                    <span style={{
                      background: 'rgba(255, 152, 0, 0.2)',
                      color: '#ff9800',
                      fontSize: '11px',
                      padding: '2px 6px',
                      borderRadius: '6px',
                      fontWeight: 600,
                    }}>
                      {weakCount} üben
                    </span>
                  )}
                </div>
                <div style={{
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: '13px',
                  marginTop: '2px',
                }}>
                  {set.pairs.length} Vokabeln
                </div>
              </div>
              <ChevronRight size={20} style={{ color: 'rgba(255,255,255,0.4)' }} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MatchGame({
  set,
  onBack,
  onComplete,
  userId,
  courseId,
  isWeaknessMode,
  roundNumber,
}: {
  set: VocabSet;
  onBack: () => void;
  onComplete: (attempts: number, pairResults: PairResult[]) => void;
  userId?: string;
  courseId?: string;
  isWeaknessMode?: boolean;
  roundNumber?: number;
}) {
  const [startTime] = useState(Date.now());
  const [isSaving, setIsSaving] = useState(false);

  // Track errors per pair (before matching)
  const [pairErrors, setPairErrors] = useState<Map<number, number>>(new Map());

  // Create shuffled items from pairs
  const items = useMemo<MatchItem[]>(() => {
    const englishItems: MatchItem[] = set.pairs.map((pair, index) => ({
      id: `en-${index}`,
      text: pair.english,
      type: 'english' as const,
      pairId: index,
    }));
    const germanItems: MatchItem[] = set.pairs.map((pair, index) => ({
      id: `de-${index}`,
      text: pair.german,
      type: 'german' as const,
      pairId: index,
    }));
    return [...shuffleArray(englishItems), ...shuffleArray(germanItems)];
  }, [set.pairs]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [matchedPairs, setMatchedPairs] = useState<Set<number>>(new Set());
  const [wrongPair, setWrongPair] = useState<[string, string] | null>(null);
  const [attempts, setAttempts] = useState(0);

  const handleSelect = async (item: MatchItem) => {
    // Already matched
    if (matchedPairs.has(item.pairId)) return;

    // Clear wrong state
    setWrongPair(null);

    // First selection
    if (!selectedId) {
      setSelectedId(item.id);
      return;
    }

    // Same item clicked
    if (selectedId === item.id) {
      setSelectedId(null);
      return;
    }

    // Find previously selected item
    const prevItem = items.find(i => i.id === selectedId);
    if (!prevItem) {
      setSelectedId(item.id);
      return;
    }

    // Same type (both english or both german) - just switch selection
    if (prevItem.type === item.type) {
      setSelectedId(item.id);
      return;
    }

    // This is an actual match attempt - count it!
    setAttempts(a => a + 1);

    // Check if match
    if (prevItem.pairId === item.pairId) {
      // Correct match!
      playSound('correct');
      const newMatched = new Set(matchedPairs);
      newMatched.add(item.pairId);
      setMatchedPairs(newMatched);
      setSelectedId(null);

      // Check if complete
      if (newMatched.size === set.pairs.length) {
        playSound('complete');
        setIsSaving(true);

        // Build pair results
        const pairResults: PairResult[] = set.pairs.map((pair, index) => ({
          pair,
          pairIndex: index,
          errorsBeforeMatch: pairErrors.get(index) || 0,
          firstTryCorrect: (pairErrors.get(index) || 0) === 0,
        }));

        // Log activity
        if (userId && courseId) {
          const elapsedSeconds = Math.round((Date.now() - startTime) / 1000);
          await logActivity(userId, 'vocab_match', {
            course_id: courseId,
            set_id: set.id,
            set_title: set.title,
            pairs_matched: set.pairs.length,
            attempts: attempts + 1,
            time_seconds: elapsedSeconds,
            is_weakness_mode: isWeaknessMode || false,
            round_number: roundNumber || 1,
          });

          // Save each pair to flashcard_progress
          for (const result of pairResults) {
            const flashcardId = `vocab_${set.id}_${result.pairIndex}`;
            await saveFlashcardResult(
              userId,
              courseId,
              flashcardId,
              result.firstTryCorrect // knew = true if 0 errors
            );
          }
        }

        setIsSaving(false);
        setTimeout(() => onComplete(attempts + 1, pairResults), 300);
      }
    } else {
      // Wrong match - track errors for BOTH involved pairs
      playSound('wrong');
      setWrongPair([selectedId, item.id]);

      // Increment error count for both pairs involved
      setPairErrors(prev => {
        const newMap = new Map(prev);
        newMap.set(prevItem.pairId, (prev.get(prevItem.pairId) || 0) + 1);
        newMap.set(item.pairId, (prev.get(item.pairId) || 0) + 1);
        return newMap;
      });

      setTimeout(() => {
        setWrongPair(null);
        setSelectedId(null);
      }, 600);
    }
  };

  const englishItems = items.filter(i => i.type === 'english');
  const germanItems = items.filter(i => i.type === 'german');

  // Show spinner while saving results
  if (isSaving) {
    return <LoadingSpinner text="Ergebnis wird gespeichert..." />;
  }

  return (
    <div style={{ padding: '16px', paddingBottom: '100px' }}>
      <button
        onClick={onBack}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          color: '#9fa8da',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          marginBottom: '16px',
          padding: 0,
          fontSize: '14px',
        }}
      >
        <ArrowLeft size={18} />
        {isWeaknessMode ? 'Abbrechen' : 'Andere Sets'}
      </button>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '16px',
      }}>
        <span style={{ fontSize: '24px' }}>{set.icon}</span>
        <h2 style={{ color: '#fff', fontSize: '18px', margin: 0 }}>
          {set.title}
          {isWeaknessMode && (
            <span style={{
              marginLeft: '8px',
              fontSize: '12px',
              color: '#ff9800',
              background: 'rgba(255,152,0,0.2)',
              padding: '2px 8px',
              borderRadius: '8px',
            }}>
              Runde {roundNumber}
            </span>
          )}
        </h2>
      </div>

      {/* Progress */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '20px',
        fontSize: '13px',
      }}>
        <span style={{ color: '#4caf50' }}>
          {matchedPairs.size} / {set.pairs.length} gefunden
        </span>
        <span style={{ color: 'rgba(255,255,255,0.5)' }}>
          {attempts} Versuche
        </span>
      </div>

      {/* Matching area */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px',
      }}>
        {/* Left column (English / Begriff) */}
        <div>
          <div style={{
            color: '#42a5f5',
            fontSize: '12px',
            fontWeight: 600,
            marginBottom: '10px',
            textAlign: 'center',
          }}>
            {courseId === 'english-test3' ? 'English' : 'Begriff'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {englishItems.map((item) => {
              const isMatched = matchedPairs.has(item.pairId);
              const isSelected = selectedId === item.id;
              const isWrong = wrongPair?.includes(item.id);

              return (
                <button
                  key={item.id}
                  onClick={() => handleSelect(item)}
                  disabled={isMatched}
                  style={{
                    padding: '14px 12px',
                    background: isMatched
                      ? 'rgba(76, 175, 80, 0.2)'
                      : isWrong
                        ? 'rgba(239, 83, 80, 0.3)'
                        : isSelected
                          ? 'rgba(255, 213, 79, 0.25)'
                          : 'rgba(66, 165, 245, 0.1)',
                    border: isMatched
                      ? '2px solid #4caf50'
                      : isWrong
                        ? '2px solid #ef5350'
                        : isSelected
                          ? '2px solid #ffd54f'
                          : '1px solid rgba(66, 165, 245, 0.3)',
                    borderRadius: '10px',
                    color: isMatched
                      ? '#4caf50'
                      : isWrong
                        ? '#ef5350'
                        : '#e8eaf6',
                    fontSize: '14px',
                    cursor: isMatched ? 'default' : 'pointer',
                    textAlign: 'center',
                    opacity: isMatched ? 0.6 : 1,
                    transition: 'all 0.15s ease',
                    textDecoration: isMatched ? 'line-through' : 'none',
                  }}
                >
                  {item.text}
                </button>
              );
            })}
          </div>
        </div>

        {/* Right column (Deutsch / Definition) */}
        <div>
          <div style={{
            color: '#ff9800',
            fontSize: '12px',
            fontWeight: 600,
            marginBottom: '10px',
            textAlign: 'center',
          }}>
            {courseId === 'english-test3' ? 'Deutsch' : 'Definition'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {germanItems.map((item) => {
              const isMatched = matchedPairs.has(item.pairId);
              const isSelected = selectedId === item.id;
              const isWrong = wrongPair?.includes(item.id);

              return (
                <button
                  key={item.id}
                  onClick={() => handleSelect(item)}
                  disabled={isMatched}
                  style={{
                    padding: '14px 12px',
                    background: isMatched
                      ? 'rgba(76, 175, 80, 0.2)'
                      : isWrong
                        ? 'rgba(239, 83, 80, 0.3)'
                        : isSelected
                          ? 'rgba(255, 213, 79, 0.25)'
                          : 'rgba(255, 152, 0, 0.1)',
                    border: isMatched
                      ? '2px solid #4caf50'
                      : isWrong
                        ? '2px solid #ef5350'
                        : isSelected
                          ? '2px solid #ffd54f'
                          : '1px solid rgba(255, 152, 0, 0.3)',
                    borderRadius: '10px',
                    color: isMatched
                      ? '#4caf50'
                      : isWrong
                        ? '#ef5350'
                        : '#e8eaf6',
                    fontSize: '14px',
                    cursor: isMatched ? 'default' : 'pointer',
                    textAlign: 'center',
                    opacity: isMatched ? 0.6 : 1,
                    transition: 'all 0.15s ease',
                    textDecoration: isMatched ? 'line-through' : 'none',
                  }}
                >
                  {item.text}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function CompletedScreen({
  set,
  attempts,
  pairResults,
  onRestart,
  onBack,
  isWeaknessMode,
  onContinueWeakness,
  hasWeaknesses,
  courseId,
  course,
  userId,
}: {
  set: VocabSet;
  attempts: number;
  pairResults: PairResult[];
  onRestart: () => void;
  onBack: () => void;
  isWeaknessMode?: boolean;
  onContinueWeakness?: () => void;
  hasWeaknesses?: boolean;
  courseId?: string;
  course?: Course | null;
  userId?: string;
}) {
  const correctCount = pairResults.filter(r => r.firstTryCorrect).length;
  const errorCount = pairResults.filter(r => !r.firstTryCorrect).length;
  const efficiency = attempts > 0 ? Math.round((set.pairs.length / attempts) * 100) : 100;

  // In weakness mode with 0 errors = mastered!
  const mastered = isWeaknessMode && errorCount === 0;

  // Recommendation
  const [recommendation, setRecommendation] = useState<Recommendation | null>(null);

  useEffect(() => {
    async function loadRecommendation() {
      if (!userId || !courseId || !course) return;

      const progress = await getCourseProgress(userId, courseId, course);
      const percentage = Math.round((correctCount / pairResults.length) * 100);
      const rec = getNextRecommendation(courseId, progress, course, {
        justCompleted: 'vocab-match',
        lastScore: percentage,
      });
      setRecommendation(rec);
    }

    loadRecommendation();
  }, [userId, courseId, course, correctCount, pairResults.length]);

  return (
    <div style={{ padding: '16px', paddingBottom: '100px', textAlign: 'center' }}>
      <div style={{ fontSize: '64px', marginBottom: '16px' }}>
        {mastered ? (
          <span style={{ fontSize: '64px' }}>🎉</span>
        ) : (
          <Trophy size={64} style={{ color: '#ffd54f' }} />
        )}
      </div>
      <h1 style={{ color: '#fff', fontSize: '24px', marginBottom: '8px' }}>
        {mastered ? 'Perfekt!' : 'Geschafft!'}
      </h1>
      <p style={{ color: '#ffd54f', fontSize: '18px', marginBottom: '16px' }}>
        {set.pairs.length} Paare in {attempts} Versuchen
      </p>

      {/* Stats summary */}
      <div style={{
        display: 'flex',
        gap: '12px',
        justifyContent: 'center',
        marginBottom: '20px',
      }}>
        <div style={{
          background: 'rgba(76, 175, 80, 0.15)',
          border: '1px solid rgba(76, 175, 80, 0.3)',
          borderRadius: '12px',
          padding: '12px 20px',
        }}>
          <div style={{ color: '#4caf50', fontSize: '24px', fontWeight: 700 }}>
            {correctCount}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
            Sofort richtig
          </div>
        </div>
        <div style={{
          background: 'rgba(239, 83, 80, 0.15)',
          border: '1px solid rgba(239, 83, 80, 0.3)',
          borderRadius: '12px',
          padding: '12px 20px',
        }}>
          <div style={{ color: '#ef5350', fontSize: '24px', fontWeight: 700 }}>
            {errorCount}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
            Mit Fehlern
          </div>
        </div>
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          padding: '12px 20px',
        }}>
          <div style={{
            color: efficiency >= 80 ? '#4caf50' : efficiency >= 60 ? '#ff9800' : '#ef5350',
            fontSize: '24px',
            fontWeight: 700
          }}>
            {efficiency}%
          </div>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
            Effizienz
          </div>
        </div>
      </div>

      {/* Pair results list */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.03)',
        borderRadius: '12px',
        padding: '12px',
        marginBottom: '24px',
        textAlign: 'left',
        maxHeight: '250px',
        overflowY: 'auto',
      }}>
        <div style={{
          fontSize: '12px',
          color: 'rgba(255,255,255,0.5)',
          marginBottom: '10px',
          fontWeight: 600,
        }}>
          ERGEBNIS PRO VOKABEL
        </div>
        {pairResults.map((result, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '8px 0',
              borderBottom: index < pairResults.length - 1
                ? '1px solid rgba(255,255,255,0.05)'
                : 'none',
            }}
          >
            {result.firstTryCorrect ? (
              <CheckCircle size={18} style={{ color: '#4caf50', flexShrink: 0 }} />
            ) : (
              <XCircle size={18} style={{ color: '#ef5350', flexShrink: 0 }} />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                color: '#e8eaf6',
                fontSize: '13px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {result.pair.english}
              </div>
              <div style={{
                color: 'rgba(255,255,255,0.5)',
                fontSize: '12px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {result.pair.german}
              </div>
            </div>
            {!result.firstTryCorrect && (
              <span style={{
                color: '#ef5350',
                fontSize: '11px',
                background: 'rgba(239, 83, 80, 0.2)',
                padding: '2px 6px',
                borderRadius: '4px',
                flexShrink: 0,
              }}>
                {result.errorsBeforeMatch}× falsch
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {/* In weakness mode with errors: continue button */}
        {isWeaknessMode && errorCount > 0 && onContinueWeakness && (
          <button
            onClick={onContinueWeakness}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '16px',
              background: '#ff9800',
              border: 'none',
              borderRadius: '12px',
              color: '#0a0e27',
              fontSize: '16px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            <RefreshCw size={20} />
            Weiter üben ({errorCount} Schwächen)
          </button>
        )}

        {/* Mastered or normal restart */}
        {mastered ? (
          <button
            onClick={onBack}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '16px',
              background: '#4caf50',
              border: 'none',
              borderRadius: '12px',
              color: '#fff',
              fontSize: '16px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            <CheckCircle size={20} />
            Fertig! Zurück zur Auswahl
          </button>
        ) : (
          <>
            <button
              onClick={onRestart}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '16px',
                background: '#ffd54f',
                border: 'none',
                borderRadius: '12px',
                color: '#0a0e27',
                fontSize: '16px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              <RotateCcw size={20} />
              Nochmal
            </button>
            <button
              onClick={onBack}
              style={{
                padding: '16px',
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '12px',
                color: '#e8eaf6',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {isWeaknessMode ? 'Beenden' : 'Anderes Set'}
            </button>
          </>
        )}
      </div>

      {/* Recommendation */}
      {recommendation && (
        <RecommendationCard recommendation={recommendation} />
      )}
    </div>
  );
}

export function VocabMatch() {
  const { courseId } = useParams<{ courseId: string }>();
  const [searchParams] = useSearchParams();
  const { course, isLoading, error } = useCourse(courseId || '');
  const { user } = useAuth();

  const [selectedSet, setSelectedSet] = useState<VocabSet | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [completedAttempts, setCompletedAttempts] = useState(0);
  const [pairResults, setPairResults] = useState<PairResult[]>([]);

  // Weakness mode state
  const [isWeaknessMode, setIsWeaknessMode] = useState(false);
  const [weaknessRound, setWeaknessRound] = useState(1);
  const [weaknessPairs, setWeaknessPairs] = useState<VocabPair[]>([]);
  // Map of setId -> number of weak pairs (for normal mode set selection)
  const [setWeaknessMap, setSetWeaknessMap] = useState<Map<string, number>>(new Map());

  // Check for weakness mode from URL
  const urlWeaknessMode = searchParams.get('weakness') === 'true';
  const urlSetId = searchParams.get('setId');
  const categoryFilter = searchParams.get('category');

  // Load weakness counts per set for normal mode
  useEffect(() => {
    async function loadSetWeaknesses() {
      if (!user?.id || !courseId || !course?.vocabSets) return;

      const progress = await getFlashcardProgress(user.id, courseId);
      const weakFlashcardIds = new Set(
        progress
          .filter((p: FlashcardProgress) => !p.knew && p.flashcard_id.startsWith('vocab_'))
          .map((p: FlashcardProgress) => p.flashcard_id)
      );

      const weaknessMap = new Map<string, number>();
      for (const set of course.vocabSets) {
        let count = 0;
        set.pairs.forEach((_, index) => {
          const flashcardId = `vocab_${set.id}_${index}`;
          if (weakFlashcardIds.has(flashcardId)) {
            count++;
          }
        });
        if (count > 0) {
          weaknessMap.set(set.id, count);
        }
      }
      setSetWeaknessMap(weaknessMap);
    }

    loadSetWeaknesses();
  }, [user?.id, courseId, course?.vocabSets]);

  // Load weakness pairs from flashcard_progress
  useEffect(() => {
    async function loadWeaknesses() {
      if (!urlWeaknessMode || !user?.id || !courseId || !course?.vocabSets) return;

      const progress = await getFlashcardProgress(user.id, courseId);

      // Find all vocab entries where knew=false
      const weakFlashcardIds = new Set(
        progress
          .filter((p: FlashcardProgress) => !p.knew && p.flashcard_id.startsWith('vocab_'))
          .map((p: FlashcardProgress) => p.flashcard_id)
      );

      // Collect weak pairs from all sets
      const allWeakPairs: VocabPair[] = [];
      const targetSet = urlSetId
        ? course.vocabSets.find(s => s.id === urlSetId)
        : null;

      const setsToCheck = targetSet ? [targetSet] : course.vocabSets;

      for (const set of setsToCheck) {
        set.pairs.forEach((pair, index) => {
          const flashcardId = `vocab_${set.id}_${index}`;
          if (weakFlashcardIds.has(flashcardId)) {
            allWeakPairs.push(pair);
          }
        });
      }

      if (allWeakPairs.length > 0) {
        setIsWeaknessMode(true);
        setWeaknessPairs(allWeakPairs);
        // Create a virtual set for weakness mode
        setSelectedSet({
          id: 'weakness-set',
          title: 'Schwächen üben',
          icon: '🔄',
          category: 'weakness',
          pairs: allWeakPairs,
        });
      }
    }

    loadWeaknesses();
  }, [urlWeaknessMode, urlSetId, user?.id, courseId, course?.vocabSets]);

  const handleGameComplete = (attempts: number, results: PairResult[]) => {
    setCompletedAttempts(attempts);
    setPairResults(results);
    setIsComplete(true);
  };

  const handleContinueWeakness = () => {
    // Get pairs that had errors
    const weakPairs = pairResults
      .filter(r => !r.firstTryCorrect)
      .map(r => r.pair);

    if (weakPairs.length > 0) {
      setWeaknessPairs(weakPairs);
      setWeaknessRound(r => r + 1);
      setSelectedSet({
        id: 'weakness-set',
        title: 'Schwächen üben',
        icon: '🔄',
        category: 'weakness',
        pairs: weakPairs,
      });
      setIsComplete(false);
      setPairResults([]);
      setCompletedAttempts(0);
    }
  };

  if (isLoading || !course) {
    return <div className="loading">Laden...</div>;
  }

  if (error) {
    return (
      <div className="error-state">
        <p>{error}</p>
        <Link to={`/course/${courseId}`} className="back-link">
          <ArrowLeft size={18} />
          Zurück
        </Link>
      </div>
    );
  }

  const allVocabSets = course.vocabSets || [];
  const vocabSets = categoryFilter
    ? allVocabSets.filter(s => s.category === categoryFilter)
    : allVocabSets;

  if (!vocabSets.length && !isWeaknessMode) {
    return (
      <div style={{ padding: '16px', textAlign: 'center' }}>
        <p style={{ color: 'rgba(255,255,255,0.6)' }}>Keine Vokabel-Sets verfügbar.</p>
        <Link to={`/course/${courseId}`} className="back-link">
          <ArrowLeft size={18} />
          Zurück
        </Link>
      </div>
    );
  }

  // Completed screen
  if (isComplete && selectedSet) {
    const hasWeaknesses = pairResults.some(r => !r.firstTryCorrect);

    return (
      <div>
        <Link
          to={`/course/${courseId}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            color: '#9fa8da',
            textDecoration: 'none',
            padding: '16px',
          }}
        >
          <ArrowLeft size={18} />
          Zurück zum Kurs
        </Link>
        <CompletedScreen
          set={selectedSet}
          attempts={completedAttempts}
          pairResults={pairResults}
          onRestart={() => {
            setIsComplete(false);
            setCompletedAttempts(0);
            setPairResults([]);
          }}
          onBack={() => {
            setSelectedSet(null);
            setIsComplete(false);
            setCompletedAttempts(0);
            setPairResults([]);
            setIsWeaknessMode(false);
            setWeaknessRound(1);
          }}
          isWeaknessMode={isWeaknessMode}
          onContinueWeakness={handleContinueWeakness}
          hasWeaknesses={hasWeaknesses}
          courseId={courseId}
          course={course}
          userId={user?.id}
        />
      </div>
    );
  }

  // Game screen
  if (selectedSet) {
    return (
      <div>
        <Link
          to={`/course/${courseId}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            color: '#9fa8da',
            textDecoration: 'none',
            padding: '16px',
          }}
        >
          <ArrowLeft size={18} />
          Zurück zum Kurs
        </Link>
        <MatchGame
          key={`${selectedSet.id}-${weaknessRound}`}
          set={selectedSet}
          onBack={() => {
            setSelectedSet(null);
            setIsWeaknessMode(false);
            setWeaknessRound(1);
          }}
          onComplete={handleGameComplete}
          userId={user?.id}
          courseId={courseId}
          isWeaknessMode={isWeaknessMode}
          roundNumber={weaknessRound}
        />
      </div>
    );
  }

  // Set selector
  return (
    <div>
      <Link
        to={`/course/${courseId}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          color: '#9fa8da',
          textDecoration: 'none',
          padding: '16px',
        }}
      >
        <ArrowLeft size={18} />
        Zurück zum Kurs
      </Link>
      <SetSelector sets={vocabSets} onSelect={setSelectedSet} weaknessMap={setWeaknessMap} />
    </div>
  );
}
